/* eslint-disable react/jsx-props-no-spreading */
import { Button, Group, Loader, Modal, Stack, Text as MantineText, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { FC, useEffect, useState } from 'react';
import CreatableMultiSelect from '../../../Atoms/inputs/CreatableMultiSelect';
import { createCustomAttribute, createCustomAttributeGroup } from '../../../Service/restapi/customAttributeService';
import { CustomAttributeS } from '../../../Service/Context/CustomAttributeS';
import { ConstantS } from '../../../Service/ConstantS';
import { MixpanelS } from '../../../Service/MixpanelS';
import { ToastS } from '../../../Service/ToastS';
import { AttributeType, CustomAttributeDTO, CustomAttributeGroupDTO } from '../../../Types/CustomAttributeT';
import { hasSpecialChar, replaceUmlaute } from '../../../Utils/utils';
import { CONTACT_FORM_ATTRIBUTE_GROUP_NAME, CustomAttributeCreationType } from './utils/FormElementsStore';

type Props = {
  attributeType: CustomAttributeCreationType | null;
  onClose: () => void;
  onCreated: (attribute: CustomAttributeDTO, groupName: string) => Promise<void>;
};

const ATTRIBUTE_TYPE_LABELS: Record<CustomAttributeCreationType, string> = {
  STRING: 'Text',
  SELECT: 'Einfachauswahl',
  TEXT: 'Mehrzeiliger Text',
  DATE: 'Datum',
  TIME: 'Uhrzeit',
  NUMBER: 'Zahl',
  BOOLEAN: 'Ja/Nein',
  MULTIPLE_CHOICE: 'Mehrfachauswahl',
};

const readJson = async <T,>(response: Response): Promise<T | null> => {
  try {
    return await response.json();
  } catch (_error) {
    return null;
  }
};

const getOrCreateAttributeGroup = async (groupName: string): Promise<CustomAttributeGroupDTO | null> => {
  const groups = await CustomAttributeS.fetchGroups('event');
  const existingGroup = groups.find(({ name }) => name === groupName);
  if (existingGroup) return existingGroup;

  const response = await createCustomAttributeGroup('event', groupName);
  if (!response.ok) return null;
  const createdGroup = await readJson<CustomAttributeGroupDTO>(response);
  if (createdGroup?.id) return createdGroup;

  const refreshedGroups = await CustomAttributeS.fetchGroups('event');
  return refreshedGroups.find(({ name }) => name === groupName) ?? null;
};

const CreateBasicCustomAttributeModal: FC<Props> = ({ attributeType, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const form = useForm({
    initialValues: { name: '', groupName: CONTACT_FORM_ATTRIBUTE_GROUP_NAME, suggestions: [] as string[] },
    validate: {
      name: (name) => {
        if (!name.trim()) return 'Bitte gib einen Namen ein.';
        if (hasSpecialChar(name)) return 'Sonderzeichen sind im Namen nicht erlaubt.';
        return null;
      },
      groupName: (groupName) => {
        if (!groupName.trim()) return 'Bitte gib eine Gruppe ein.';
        if (hasSpecialChar(groupName)) return 'Sonderzeichen sind im Gruppennamen nicht erlaubt.';
        return null;
      },
      suggestions: (suggestions) =>
        attributeType === 'SELECT' && suggestions.length === 0 ? 'Bitte gib mindestens einen Vorschlag ein.' : null,
    },
  });

  useEffect(() => {
    if (attributeType) form.reset();
    // The form instance is stable; only reset when a creation flow is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeType]);

  const create = async ({ name, groupName, suggestions }: typeof form.values) => {
    if (!attributeType) return;
    setLoading(true);

    try {
      const group = await getOrCreateAttributeGroup(groupName.trim());
      if (!group) throw new Error('Could not create the custom attribute group');

      const selectable = attributeType === 'SELECT';
      const persistedAttributeType: AttributeType = selectable ? 'STRING' : attributeType;
      const persistedSuggestions = selectable ? suggestions : [];
      const response = await createCustomAttribute(
        name.trim(),
        '',
        persistedAttributeType,
        group.id,
        selectable,
        persistedSuggestions,
      );
      if (!response.ok) throw new Error('Could not create the custom attribute');

      let attribute = await readJson<CustomAttributeDTO>(response);
      if (!attribute?.id) {
        const attributes = await CustomAttributeS.fetchGroupAttributes(group.id);
        attribute =
          [...attributes]
            .reverse()
            .find(
              (candidate) =>
                candidate.name === name.trim() &&
                candidate.attributeType === persistedAttributeType &&
                candidate.selectable === selectable,
            ) ?? null;
      }
      if (!attribute) throw new Error('The created custom attribute could not be loaded');

      await onCreated(
        {
          ...attribute,
          name: attribute.name ?? name.trim(),
          attributeType: attribute.attributeType ?? persistedAttributeType,
          selectable,
          suggestions: selectable ? persistedSuggestions : attribute.suggestions ?? [],
        },
        group.name,
      );
      MixpanelS.track(ConstantS.TrackingEvents.CustomAttibuteCreated);
      ToastS.success('field-created', 'Eigenes Feld erstellt und zum Formular hinzugefügt');
      onClose();
    } catch (_error) {
      ToastS.generalError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      centered
      closeOnClickOutside={false}
      closeOnEscape={!loading}
      withCloseButton={!loading}
      opened={attributeType !== null}
      onClose={() => {
        if (!loading) onClose();
      }}
      title={<MantineText weight="bolder">Eigenes Formularfeld erstellen</MantineText>}
    >
      <form onSubmit={form.onSubmit(create)}>
        <Stack spacing="sm">
          <MantineText color="dimmed" size="sm">
            Nach dem Speichern wird das eigene Feld angelegt und direkt zum Kontaktformular hinzugefügt.
          </MantineText>
          <TextInput
            required
            autoFocus
            label="Name"
            placeholder="z. B. Anzahl der Gäste"
            {...form.getInputProps('name')}
            onChange={(event) => form.setFieldValue('name', replaceUmlaute(event.currentTarget.value))}
          />
          <TextInput disabled label="Typ" value={attributeType ? ATTRIBUTE_TYPE_LABELS[attributeType] : ''} />
          {attributeType === 'SELECT' && (
            <CreatableMultiSelect
              required
              creatable
              searchable
              label="Vorschläge"
              placeholder="Vorschlag eingeben und bestätigen"
              data={form.values.suggestions}
              value={form.values.suggestions}
              error={form.errors.suggestions}
              onChange={(suggestions) => form.setFieldValue('suggestions', suggestions)}
            />
          )}
          <TextInput
            required
            label="Gruppe"
            {...form.getInputProps('groupName')}
            onChange={(event) => form.setFieldValue('groupName', replaceUmlaute(event.currentTarget.value))}
          />
          <Group position="right" mt="sm">
            <Button variant="default" onClick={onClose} disabled={loading}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading} rightIcon={loading ? <Loader size="xs" /> : undefined}>
              Speichern
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default CreateBasicCustomAttributeModal;
